import { useClients } from '@repo/queries';
import { Mail, Phone, Plus, Search, Users } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { ClientAvatar } from '@/features/avatar';
import { inferClientTag } from '@/features/avatar';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function ClientsListScreen() {
  const navigate = useNavigate();
  const { canEditOrg } = useActiveRole();
  const { clients, loading, fetchNextPage, hasNextPage, isFetchingNextPage } = useClients();
  const [search, setSearch] = useState('');
  const deferred = useDeferredValue(search);

  const filtered = useMemo(() => {
    const q = deferred.trim().toLowerCase();
    if (!q) return clients ?? [];
    return (clients ?? []).filter((client) => {
      const haystack = [
        client.name,
        client.displayName ?? '',
        client.company ?? '',
        (client.contact?.email as string | undefined) ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, deferred]);

  return (
    <Page>
      <PageHeader
        title="Clients"
        description="Everyone you bill, with their contact details and history."
        actions={
          canEditOrg ? (
            <Button asChild>
              <Link to="/clients/new">
                <Plus className="size-4" /> New client
              </Link>
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex w-full items-center gap-2 sm:max-w-sm">
        <div className="relative w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search clients…"
            className="pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search clients"
          />
        </div>
      </div>

      {loading && (clients ?? []).length === 0 ? (
        <ClientsLoading />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Users className="size-6" />}
          title={search ? 'No matches' : 'No clients yet'}
          description={
            search
              ? 'Try a different name, company, or email.'
              : 'Create your first client to start sending invoices.'
          }
          actions={
            search || !canEditOrg ? null : (
              <Button onClick={() => navigate('/clients/new')}>
                <Plus className="size-4" /> Add a client
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((client) => {
            const email = (client.contact?.email as string | undefined) ?? null;
            const phone = (client.contact?.phone as string | undefined) ?? null;
            return (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="block rounded-xl border border-line bg-card p-5 transition-colors hover:border-line-strong"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <ClientAvatar
                      name={client.name}
                      photoUrl={client.photoUrl ?? null}
                      tag={inferClientTag({ name: client.name, company: client.company })}
                      size={44}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {client.name}
                      </p>
                      {client.company ? (
                        <p className="truncate text-xs text-muted-foreground">
                          {client.company}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {!client.isActive ? (
                    <Badge variant="outline">Archived</Badge>
                  ) : client.defaultTaxExempt ? (
                    <Badge variant="default">Tax exempt</Badge>
                  ) : null}
                </div>
                <div className="mt-4 flex flex-col gap-1.5 text-xs text-muted-foreground">
                  {email ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="size-3.5" /> {email}
                    </span>
                  ) : null}
                  {phone ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Phone className="size-3.5" /> {phone}
                    </span>
                  ) : null}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {hasNextPage ? (
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            loading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </Page>
  );
}

function ClientsLoading() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, idx) => (
        <Skeleton key={idx} className="h-32 rounded-xl" />
      ))}
    </div>
  );
}
